import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { ArrowLeft, Trophy, Minus, Plus, Check } from "lucide-react";
import { toast } from "sonner";
import { TEAM_COLORS } from "@/lib/teamColors";

function defaultName() {
  const now = new Date();
  const dd = String(now.getDate()).padStart(2, "0");
  const mm = String(now.getMonth() + 1).padStart(2, "0");
  return `Turnier am ${dd}.${mm}.${now.getFullYear()}`;
}

export default function TournamentCreate() {
  const location = useLocation();
  const navigate = useNavigate();
  const passedTeams = location.state?.teams || null;
  const fromBalance = Array.isArray(passedTeams) && passedTeams.length >= 2;
  const playerPool = location.state?.playerPool || null;
  const balanceCriterion = location.state?.balanceCriterion || "overall";
  const canChanging = fromBalance && Array.isArray(playerPool) && playerPool.length > 0;

  const colorLabel = (key) => (TEAM_COLORS.find((c) => c.key === key)?.label) || "";

  const [name, setName] = useState("");
  const [mode, setMode] = useState("hin");
  const [pitchCount, setPitchCount] = useState(1);
  const [teamCount, setTeamCount] = useState(fromBalance ? passedTeams.length : 4);
  const [teams, setTeams] = useState([]); // [{name, color, players?}]
  const [saving, setSaving] = useState(false);

  // Initial teams
  useEffect(() => {
    if (fromBalance) {
      setTeams(
        passedTeams.map((t, i) => ({
          name: t.name,
          color: TEAM_COLORS[i % TEAM_COLORS.length].key,
          players: t.players || [],
        }))
      );
      setName(defaultName());
    } else {
      setName(defaultName());
      setTeams(Array.from({ length: teamCount }).map((_, i) => ({
        name: `Team ${i + 1}`,
        color: TEAM_COLORS[i % TEAM_COLORS.length].key,
        players: [],
      })));
    }
  }, [fromBalance]);

  // Sync teams array length when teamCount changes (only in standalone mode)
  useEffect(() => {
    if (fromBalance) return;
    setTeams((prev) => {
      if (prev.length === teamCount) return prev;
      if (prev.length < teamCount) {
        const next = [...prev];
        for (let i = prev.length; i < teamCount; i++) {
          next.push({ name: `Team ${i + 1}`, color: TEAM_COLORS[i % TEAM_COLORS.length].key, players: [] });
        }
        return next;
      }
      return prev.slice(0, teamCount);
    });
  }, [teamCount, fromBalance]);

  const gamesHin = useMemo(() => (teams.length * (teams.length - 1)) / 2, [teams.length]);
  const totalGames = mode === "hin_rueck" ? gamesHin * 2 : gamesHin;

  const changeMode = (m) => {
    setMode(m);
    // Beim Wechsel zu "changing": kontrastreiche Farben + Benennung nach Farbe (Team Blau, Team Rot, …).
    if (m === "changing") {
      const order = ["blau", "rot", "gruen", "gelb", "orange", "pink", "hellblau", "weiss"];
      setTeams((prev) => prev.map((t, i) => {
        const colorKey = (TEAM_COLORS.find((c) => c.key === order[i % order.length]) || TEAM_COLORS[i % TEAM_COLORS.length]).key;
        return { ...t, color: colorKey, name: `Team ${colorLabel(colorKey)}`.trim() };
      }));
    }
  };

  const setTeamField = (idx, field, value) => {
    setTeams((prev) => prev.map((t, i) => (i === idx ? { ...t, [field]: value } : t)));
  };

  const handleCreate = async () => {
    const cleanName = name.trim();
    if (!cleanName) { toast.error("Bitte einen Turniernamen eingeben"); return; }
    if (teams.length < 2) { toast.error("Mindestens 2 Teams benötigt"); return; }
    if (mode === "changing" && teams.length % 2 !== 0) {
      toast.error("Changing Teams benötigt eine gerade Team-Anzahl"); return;
    }
    for (const t of teams) {
      if (!t.name.trim()) { toast.error("Alle Teams brauchen einen Namen"); return; }
    }
    setSaving(true);
    try {
      const payload = {
        name: cleanName,
        mode,
        pitch_count: pitchCount,
        teams: teams.map((t) => ({
          name: t.name.trim(),
          color: t.color || null,
          // Im Modus "changing" sind die Teams nur Farb-Slots; Spieler kommen pro Spieltag.
          players: mode === "changing" ? [] : (t.players || []).map((p) => ({
            id: p.id, name: p.name, position: p.position,
            jahrgang: p.jahrgang, overall: p.overall, talent: p.talent,
          })),
        })),
      };
      if (mode === "changing") {
        payload.balance_criterion = balanceCriterion;
        payload.player_pool = (playerPool || []).map((p) => ({
          id: p.id, name: p.name, position: p.position,
          jahrgang: p.jahrgang, overall: p.overall, talent: p.talent,
        }));
      }
      const { data } = await api.post("/tournaments", payload);
      toast.success("Turnier erstellt");
      navigate(`/turniere/${data.id}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erstellen fehlgeschlagen");
    } finally { setSaving(false); }
  };

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="tc-page">
      <button
        onClick={() => navigate(-1)}
        className="text-xs uppercase tracking-wider text-zinc-500 hover:text-brand-lighter transition flex items-center gap-1 mb-6"
        data-testid="tc-back-btn"
      >
        <ArrowLeft className="h-3 w-3" /> Zurück
      </button>

      <div className="mb-8">
        <div className="text-[10px] uppercase tracking-wider text-brand-lighter mb-2">Neues Turnier</div>
        <h1 className="font-display text-3xl sm:text-4xl font-bold uppercase tracking-tight">Turnier erstellen</h1>
        <p className="text-zinc-400 text-sm mt-2">
          {fromBalance
            ? `${teams.length} Teams werden direkt aus der Team-Balance übernommen.`
            : "Lege Anzahl Teams, Spielfelder und Modus fest. Spielerauswahl ist optional."}
        </p>
      </div>

      <div className="border border-zinc-800 bg-zinc-900/50 p-6 space-y-6">
        {/* Turniername */}
        <div className="space-y-2">
          <Label className="text-[10px] uppercase tracking-wide text-zinc-400">Turniername</Label>
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="z. B. Sommerturnier"
            className="bg-zinc-950 border-zinc-800 text-zinc-100 focus-visible:ring-brand-lighter h-12 text-base"
            data-testid="tc-name-input"
          />
        </div>

        {/* Anzahl Teams (nur Standalone) */}
        {!fromBalance && (
          <div className="space-y-2">
            <Label className="text-[10px] uppercase tracking-wide text-zinc-400">Anzahl Teams</Label>
            <NumStepper
              value={teamCount}
              onChange={setTeamCount}
              min={2}
              max={16}
              testIdPrefix="tc-teamcount"
            />
          </div>
        )}

        {/* Anzahl Spielfelder */}
        <div className="space-y-2">
          <Label className="text-[10px] uppercase tracking-wide text-zinc-400">Anzahl Spielfelder</Label>
          <NumStepper
            value={pitchCount}
            onChange={setPitchCount}
            min={1}
            max={8}
            testIdPrefix="tc-pitchcount"
          />
          <div className="text-[11px] text-zinc-500">Parallel-Spielfelder pro Runde. Wird als Spielfeld-Nr. in jedem Spiel angezeigt.</div>
        </div>

        {/* Spielmodus */}
        <div className="space-y-3">
          <Label className="text-[10px] uppercase tracking-wide text-zinc-400">Spielmodus</Label>
          <RadioGroup value={mode} onValueChange={changeMode}>
            <label
              htmlFor="mode-hin"
              className={
                "flex items-start gap-3 p-4 border cursor-pointer transition " +
                (mode === "hin" ? "border-brand-lighter/60 bg-brand-lighter/5" : "border-zinc-800 bg-zinc-950 hover:border-zinc-700")
              }
              data-testid="tc-mode-hin"
            >
              <RadioGroupItem value="hin" id="mode-hin" className="border-zinc-600 text-brand-lighter mt-0.5" />
              <div>
                <div className="font-medium text-zinc-100">Hinrunde</div>
                <div className="text-xs text-zinc-500">Jedes Team spielt einmal gegen jedes andere Team.</div>
              </div>
            </label>
            <label
              htmlFor="mode-hr"
              className={
                "flex items-start gap-3 p-4 border cursor-pointer transition " +
                (mode === "hin_rueck" ? "border-brand-lighter/60 bg-brand-lighter/5" : "border-zinc-800 bg-zinc-950 hover:border-zinc-700")
              }
              data-testid="tc-mode-hin-rueck"
            >
              <RadioGroupItem value="hin_rueck" id="mode-hr" className="border-zinc-600 text-brand-lighter mt-0.5" />
              <div>
                <div className="font-medium text-zinc-100">Hin- und Rückrunde</div>
                <div className="text-xs text-zinc-500">Jedes Team spielt zweimal gegen jedes andere Team.</div>
              </div>
            </label>
            {canChanging && (
              <label
                htmlFor="mode-changing"
                className={
                  "flex items-start gap-3 p-4 border cursor-pointer transition " +
                  (mode === "changing" ? "border-brand-lighter/60 bg-brand-lighter/5" : "border-zinc-800 bg-zinc-950 hover:border-zinc-700")
                }
                data-testid="tc-mode-changing"
              >
                <RadioGroupItem value="changing" id="mode-changing" className="border-zinc-600 text-brand-lighter mt-0.5" />
                <div>
                  <div className="font-medium text-zinc-100">Changing Teams</div>
                  <div className="text-xs text-zinc-500">Spieler werden vor jedem Spieltag neu auf die Teams verteilt. Die Tabelle wertet einzelne Spieler. Gerade Team-Anzahl nötig.</div>
                </div>
              </label>
            )}
          </RadioGroup>
        </div>

        {/* Teams: Name + Farbe */}
        <div className="space-y-2">
          <Label className="text-[10px] uppercase tracking-wide text-zinc-400">Teams</Label>
          <div className="space-y-2">
            {teams.map((t, idx) => {
              const activeColor = TEAM_COLORS.find((c) => c.key === t.color) || TEAM_COLORS[idx % TEAM_COLORS.length];
              return (
                <div
                  key={idx}
                  className="border border-zinc-800 bg-zinc-950 p-3 space-y-2"
                  data-testid={`tc-team-${idx}`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`h-8 w-8 rounded-md shrink-0 ${activeColor.cls}`} title={activeColor.key} />
                    <Input
                      value={t.name}
                      onChange={(e) => setTeamField(idx, "name", e.target.value)}
                      placeholder={`Team ${idx + 1}`}
                      className="bg-zinc-900 border-zinc-800 text-zinc-100 focus-visible:ring-brand-lighter h-10"
                      data-testid={`tc-team-name-${idx}`}
                    />
                    <span className="text-[10px] uppercase tracking-wider text-zinc-500 shrink-0">
                      {(t.players || []).length} Sp.
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {TEAM_COLORS.map((c) => {
                      const active = t.color === c.key;
                      return (
                        <button
                          key={c.key}
                          type="button"
                          onClick={() => setTeamField(idx, "color", c.key)}
                          className={
                            "h-7 w-7 rounded-md transition flex items-center justify-center " +
                            c.cls + " " +
                            (active ? "ring-2 ring-offset-2 ring-offset-zinc-950 ring-white" : "opacity-80 hover:opacity-100")
                          }
                          data-testid={`tc-team-color-${idx}-${c.key}`}
                          aria-label={`Farbe ${c.key}`}
                        >
                          {active && <Check className="h-3.5 w-3.5 text-zinc-950" />}
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="border-t border-zinc-800 pt-5 grid grid-cols-3 gap-4 text-sm">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-zinc-500">Teams</div>
            <div className="font-display text-2xl font-bold text-zinc-100">{teams.length}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-zinc-500">Spiele gesamt</div>
            <div className="font-display text-2xl font-bold text-brand-lighter" data-testid="tc-game-count">{totalGames}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-zinc-500">Spielfelder</div>
            <div className="font-display text-2xl font-bold text-zinc-100">{pitchCount}</div>
          </div>
        </div>

        <Button
          onClick={handleCreate}
          disabled={saving}
          className="w-full bg-brand hover:bg-brand-light text-white uppercase tracking-wider font-semibold rounded-sm h-12"
          data-testid="tc-create-btn"
        >
          <Trophy className="h-4 w-4 mr-2" />
          {saving ? "Erstellen…" : "Turnier starten"}
        </Button>
      </div>
    </div>
  );
}

function NumStepper({ value, onChange, min, max, testIdPrefix }) {
  return (
    <div className="flex items-center gap-2">
      <button
        type="button"
        onClick={() => onChange(Math.max(min, value - 1))}
        disabled={value <= min}
        className="h-11 w-11 rounded-md bg-zinc-800 hover:bg-zinc-700 disabled:opacity-40 text-zinc-100 flex items-center justify-center"
        data-testid={`${testIdPrefix}-minus`}
        aria-label="Weniger"
      >
        <Minus className="h-4 w-4" />
      </button>
      <div className="flex-1 h-11 rounded-md bg-zinc-950 border border-zinc-800 flex items-center justify-center font-display text-lg font-bold text-zinc-100 tabular-nums" data-testid={`${testIdPrefix}-value`}>
        {value}
      </div>
      <button
        type="button"
        onClick={() => onChange(Math.min(max, value + 1))}
        disabled={value >= max}
        className="h-11 w-11 rounded-md bg-brand hover:bg-brand-light disabled:opacity-40 text-white flex items-center justify-center"
        data-testid={`${testIdPrefix}-plus`}
        aria-label="Mehr"
      >
        <Plus className="h-4 w-4" />
      </button>
    </div>
  );
}
